import React from 'react';
import { LifeBuoy, Mail, Banana, BotOff, MegaphoneOff, FlagOff } from 'lucide-react'; // Example icon import
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const rules = [
  {
    icon: <Banana /> ,
    rule: "No inappropriate content: Avoid offensive, hateful, or explicit imagery."
  },
  {
    icon: <BotOff /> ,
    rule: "Botting is not allowed, any botting activity like multiple accounts or use of bots for artwork will be banned."
  },
  {
    icon: <MegaphoneOff /> ,
    rule: "No political or religious content: Keep the canvas a neutral space for all users."
  },
  { 
    icon: <FlagOff /> ,
    rule: "No spam: Avoid repetitive or meaningless patterns that detract from the canvas experience."
  },
]

const faqs = [
  {
    question: "How do I claim a pixel?",
    answer:
      "Sign in to your account, zoom into the canvas, and click on any pixel, if it's unclaimed or the expiry has passed, you can claim it by spending Pixel Drops.",
  },
  {
    question: "Can I edit someone else's artwork?",
    answer:
      "No. Only the owner of a artwork can edit it. However, you can view or get inspired by others' artwork.",
  },
  {
    question: "How long do I own an artwork?",
    answer:
      "Each artwork has a time-based expiration. You’ll need to renew it before it expires to maintain ownership. By clicking on you artworj, you can see the expiry time and extend it using Pixel Drops.",
  },
  {
    question: "What happens when an artwork expires?",
    answer:
      "Expired artwork become available for others to claim and customize, making the canvas continuously dynamic.",
  },
  {
    question: "How do I report inappropriate content?",
    answer:
      "Click on the artwork, and you’ll see a report option.",
  },
  {
    question: "How do I report inappropriate canvases?",
    answer:
      "I have a list of banned canvases and will continue to add to it. If you see a canvas that seems inapropriate, report any artwork in the canvas.",
  },
];

const AboutPage = () => {
  const SUPPORT_EMAIL = "artparcel.sup@gmail.com"; // ← replace with your Gmail

  return (
    <div className="bg-slate-900 text-white min-h-screen">
      <Helmet>
        <title>About | ArtParcel</title>
        <meta name="description" content="Learn more about ArtParcel, the collaborative pixel art platform. Claim pixels, express creativity, and explore the community-driven canvas." />
        <meta name="keywords" content="pixel art, collaborative canvas, artwork editor, NFT-style grid, art ownership, digital art platform" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="About ArtParcel" />
        <meta property="og:description" content="ArtParcel lets you own and edit artwork in a massive shared canvas. Learn how it works." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourwebsite.com/about" />
        <meta property="og:image" content="https://yourwebsite.com/preview.jpg" />
      </Helmet>

      <main className="flex-1">
        <div className="max-w-4xl mx-auto py-24 px-4">
          <h1 className="text-4xl font-bold mb-6 text-center text-white">About ArtParcel</h1>
          <section className="mb-16">
            <h2 className="text-3xl font-semibold text-white mb-6">RULES</h2>
            <dl className="space-y-6">
              {rules.map(({ icon, rule }, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <dt className="text-xl font-semibold text-red-400 py-2">{icon}</dt>
                  <dd className="text-gray-300 mt-2">{rule}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-semibold text-white mb-6">Frequently Asked Questions</h2>
            <dl className="space-y-6">
              {faqs.map(({ question, answer }, index) => (
                <div key={index}>
                  <dt className="text-xl font-semibold text-blue-400">{question}</dt>
                  <dd className="text-gray-300 mt-2">{answer}</dd>
                </div>
              ))}
            </dl>
          </section>

          {/*
          <section className="mb-16">
            <h2 className="text-3xl font-semibold text-white mb-6">Support</h2>

            <div className="grid gap-6 sm:grid-cols-2">
              {/* Left: blurb }
              <div className="rounded-2xl border border-zinc-700 bg-slate-800/60 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="rounded-xl bg-indigo-600/20 p-2">
                    <LifeBuoy className="h-6 w-6 text-indigo-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">We’re here to help</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">
                  Found a bug, have a feature idea, or need a hand? Visit our Support page to send us a message.
                  Our tiny-but-mighty team reads every request.
                </p>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  {/* Primary CTA: internal route to Support page }
                  <Link
                    to="/Support"
                    className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/60"
                  >
                    Open Support
                  </Link>

                  {/* Secondary: email fallback }
                  <a
                    href={`mailto:${SUPPORT_EMAIL}?subject=ArtParcel%20Support%20Request`}
                    className="inline-flex items-center gap-2 rounded-lg border border-zinc-600 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/60"
                  >
                    <Mail className="h-4 w-4" />
                    {SUPPORT_EMAIL}
                  </a>
                </div>
              </div>

              {/* Right: quick tips / expectations }
              <div className="rounded-2xl border border-zinc-700 bg-slate-800/40 p-6">
                <h3 className="text-lg font-semibold text-white mb-3">Before you write</h3>
                <ul className="list-disc pl-5 space-y-2 text-gray-300">
                  <li>Tell us what page or feature you were using.</li>
                  <li>Include steps to reproduce the issue, if possible.</li>
                  <li>Screenshots or short clips are super helpful.</li>
                </ul>
                <p className="text-xs text-zinc-400 mt-4">
                  You can also check our <Link to="/Terms/Refunds" className="underline hover:text-white">Refund Policy</Link> and other pages in the footer.
                </p>
              </div>
            </div>
          </section>
          */}
        </div>
      </main>
      
      {/*
      <footer className="border-t border-slate-800 bg-slate-900/80">
        <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-sm text-slate-400">
            © {new Date().getFullYear()} ArtParcel
          </div>

          <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">

            {/* Terms / Privacy / Refunds — point these to your routes or external pages }
            <a
              href="/Terms/Terms"
              className="text-slate-300 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/60 rounded"
            >
              Terms
            </a>
            <a
              href="/Terms/Privacy"
              className="text-slate-300 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/60 rounded"
            >
              Privacy
            </a>
            <a
              href="/Terms/Refunds"
              className="text-slate-300 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/60 rounded"
            >
              Refund Policy
            </a>

            {/* Support Gmail (mailto with a prefilled subject) }
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=ArtParcel%20Support%20Request`}
              className="text-slate-300 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/60 rounded"
            >
              {SUPPORT_EMAIL}
            </a>
          </nav>
        </div>
      </footer>
          */}
    </div>
  );
};

export default AboutPage;

